if (!customElements.get('flipzy-hero-player')) {
  customElements.define('flipzy-hero-player', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      this.video = this.querySelector('video');
      this.poster = this.querySelector('.flipzy-hero__poster');
      this.toggle = this.querySelector('.flipzy-hero__video-toggle');
      if (!this.video) return;

      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.visible = false;
      this.loaded = false;
      this.failed = false;
      this.userPaused = false;
      this.starting = false;
      this.attempt = 0;
      this.video.muted = this.video.defaultMuted = true;
      const listeners = [];
      const listen = (target, type, handler) => {
        if (!target?.addEventListener) return;
        target.addEventListener(type, handler);
        listeners.push(() => target.removeEventListener(type, handler));
      };
      const update = () => this.update();
      listen(this.poster, 'load', update);
      listen(this.poster, 'error', update);
      listen(this.motion, 'change', update);
      listen(this.connection, 'change', update);
      listen(window, 'online', update);
      listen(window, 'offline', update);
      listen(document, 'visibilitychange', update);
      listen(this.video, 'playing', () => {
        if (!this.canPlay() || this.userPaused || this.failed) return;
        this.starting = false;
        this.clearDeadline();
        this.setAttribute('data-playing', '');
        this.toggle.hidden = false;
        this.updateToggle();
      });
      listen(this.video, 'waiting', () => {
        if (!this.loaded || !this.canPlay() || this.userPaused || this.failed) return;
        this.removeAttribute('data-playing');
        // A buffered frame must not leave a stalled hero downloading indefinitely.
        if (!this.deadline) this.deadline = setTimeout(() => this.fallback(), 3500);
      });
      listen(this.video, 'error', () => {
        if (this.loaded) this.fallback();
      });
      listen(this.toggle, 'click', () => {
        this.userPaused = !this.userPaused;
        this.updateToggle();
        this.update();
      });

      let observer;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.update();
        }, { threshold: 0.01 });
        observer.observe(this);
      } else {
        this.visible = true;
        this.update();
      }
      this.cleanup = () => {
        observer?.disconnect();
        listeners.forEach(remove => remove());
        this.unload();
      };
    }

    constrained() {
      const connection = this.connection;
      return this.motion.matches || navigator.onLine === false || connection?.saveData ||
        ['slow-2g', '2g'].includes(connection?.effectiveType) ||
        (typeof connection?.downlink === 'number' && connection.downlink < 0.8);
    }

    canPlay() {
      return this.visible && !document.hidden && !this.constrained();
    }

    chooseSource() {
      const connection = this.connection;
      const small = window.matchMedia('(max-width: 749px)').matches ||
        connection?.effectiveType === '3g' ||
        (typeof connection?.downlink === 'number' && connection.downlink < 3);
      const variant = small ? 'mobile' : 'desktop';
      const candidates = [...this.video.querySelectorAll('source[data-src]')].filter(source =>
        this.video.canPlayType(source.type) && (!source.dataset.variant || source.dataset.variant === variant)
      );
      // Shopify-hosted overrides can contain several MP4 renditions. Prefer a
      // suitable resolution rather than accidentally loading its largest file.
      const sized = candidates.filter(source => Number(source.dataset.width) > 0);
      if (sized.length) {
        const width = small ? 768 : 1280;
        sized.sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
        return (sized.filter(source => Number(source.dataset.width) <= width).pop() || sized[0]).dataset.src;
      }
      return candidates[0]?.dataset.src;
    }

    update() {
      if (this.constrained()) {
        this.unload();
        return;
      }
      if (!this.canPlay() || this.userPaused || this.failed) {
        this.suspend();
        return;
      }
      if (this.poster && !this.poster.complete) return;
      if (this.starting || !this.video.paused) return;
      this.starting = true;
      // Give the responsive, high-priority poster a paint before fetching video.
      this.paintFrame = requestAnimationFrame(() => {
        this.paintFrame = requestAnimationFrame(() => this.start());
      });
    }

    start() {
      this.paintFrame = null;
      if (!this.canPlay() || this.userPaused || this.failed) {
        this.suspend();
        return;
      }
      if (!this.loaded) {
        const source = this.chooseSource();
        if (!source) {
          this.fallback();
          return;
        }
        this.loaded = true;
        this.video.preload = 'auto';
        this.video.src = source;
        this.video.load();
      }
      const attempt = ++this.attempt;
      this.clearDeadline();
      this.deadline = setTimeout(() => this.fallback(), 6500);
      try {
        this.video.play()?.catch(() => {
          if (attempt === this.attempt && this.canPlay() && !this.userPaused) this.fallback();
        });
      } catch (_) {
        this.fallback();
      }
    }

    updateToggle() {
      if (!this.toggle) return;
      this.toggle.toggleAttribute('data-paused', this.userPaused);
      this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
    }

    clearDeadline() {
      clearTimeout(this.deadline);
      this.deadline = null;
    }

    suspend() {
      ++this.attempt;
      this.starting = false;
      cancelAnimationFrame(this.paintFrame);
      this.paintFrame = null;
      this.clearDeadline();
      this.video.pause();
    }

    unload() {
      this.suspend();
      this.removeAttribute('data-playing');
      if (this.toggle) this.toggle.hidden = true;
      if (!this.loaded) return;
      this.loaded = false;
      this.video.removeAttribute('src');
      this.video.preload = 'none';
      this.video.load();
    }

    fallback() {
      // Do not retry on scroll/resize: a failed connection keeps the photograph.
      this.failed = true;
      this.unload();
    }

    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }
  });
}
